import { Link, useLocation, useNavigate } from 'react-router';
import {
  BellIcon,
  BookOpenIcon,
  CaretDownIcon,
  GearSixIcon,
  HeadsetIcon,
  PrinterIcon,
  SignOutIcon,
  TableIcon,
} from '@phosphor-icons/react';
import { Button, Popover, cn, sideNavRowStyles } from '@skupreme/shared/ui';

import { useAuth } from '@contexts/auth';
import { useSidebar } from '@contexts/sidebar';

import { getFullName, getInitials } from './user';
import type { MenuItem } from './utils';

const supportPortalUrl =
  'https://skupreme.atlassian.net/helpcenter/POR/user/requests';
const knowledgeBaseUrl = 'https://skupreme.com/knowledge-base';

/* The row shape the organization list already stands its actions on. */
const LIST_ROW = cn(
  'sk:h-auto sk:shrink-0 sk:gap-2 sk:py-2',
  'sk:text-dense sk:font-normal sk:text-fg',
);

function UserAvatar({
  imageUrl,
  initials,
}: {
  imageUrl?: string;
  initials: string;
}) {
  return (
    <span className="sk:bg-surface sk:inset-shadow-hairline sk:flex sk:size-8 sk:shrink-0 sk:items-center sk:justify-center sk:overflow-hidden sk:rounded-full">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="sk:size-full sk:object-cover" />
      ) : (
        <span className="sk:text-fg-secondary sk:text-xs sk:font-semibold">
          {initials}
        </span>
      )}
    </span>
  );
}

export type ProfileMenuProps = {
  /** Pages pinned above Settings. */
  pages: MenuItem[];
  /** Pages listed inside the account menu, after Notifications. */
  accountPages: MenuItem[];
  /** EN-4134: a V2 account; the legacy-only entries (notifications feed, ShipNode, prediction export) hide. */
  v2?: boolean;
};

/** The pages pinned below the nav list, and the account menu. */
export function ProfileMenu({
  pages,
  accountPages,
  v2 = false,
}: ProfileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { openExportPopup } = useSidebar() as {
    openExportPopup: () => void;
  };

  const fullName = getFullName(user?.first_name, user?.last_name);
  const initials = getInitials(user?.first_name, user?.last_name);
  const imageUrl = (user?.image as { url?: string } | undefined)?.url;

  const isSettingsActive = location.pathname.startsWith('/app/settings');

  return (
    <div className="sk:flex sk:flex-col sk:gap-0.5">
      {pages.map(page => (
        <Link
          key={page.link}
          to={`${page.link}/`}
          className={sideNavRowStyles(
            location.pathname.startsWith(`${page.link}/`),
          )}
        >
          <span className="sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center">
            {page.phosphorIcon}
          </span>
          <span className="sk:flex-1 sk:truncate">{page.text}</span>
        </Link>
      ))}

      <Link to="/app/settings/" className={sideNavRowStyles(isSettingsActive)}>
        <span className="sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center">
          <GearSixIcon size={18} />
        </span>
        <span className="sk:flex-1 sk:truncate">Settings</span>
      </Link>

      <Popover
        aria-label="Account"
        placement="top start"
        className="sk:w-(--trigger-width) sk:min-w-64"
        trigger={
          <Button
            variant="menu"
            aria-label="Account menu"
            /* `py-1` rather than the organization switcher's `py-1.5`: this
               trigger stacks two text lines instead of one, so a lighter
               vertical pad still lands on the same overall row height, the
               avatar being the taller of the two. */
            className="sk:h-auto sk:gap-2 sk:px-2 sk:py-1"
          >
            <UserAvatar imageUrl={imageUrl} initials={initials} />
            <span className="sk:min-w-0 sk:flex-1 sk:text-left">
              <span className="sk:text-fg sk:block sk:truncate">
                {fullName}
              </span>
              <span className="sk:text-fg-tertiary sk:block sk:truncate sk:text-xs">
                {user?.email}
              </span>
            </span>
            <CaretDownIcon size={14} />
          </Button>
        }
      >
        {({ close }) => (
          <div className="sk:flex sk:flex-col sk:gap-1">
            {!v2 && (
              <Button
                variant="menu"
                className={LIST_ROW}
                onPress={() => {
                  close();
                  navigate('/app/notifications/');
                }}
              >
                <BellIcon size={18} />
                Notifications
              </Button>
            )}
            {accountPages.map(page => (
              <Button
                key={page.link}
                variant="menu"
                className={LIST_ROW}
                onPress={() => {
                  close();
                  navigate(`${page.link}/`);
                }}
              >
                {page.phosphorIcon}
                {page.text}
              </Button>
            ))}
            <Button
              variant="menu"
              className={LIST_ROW}
              onPress={() => {
                close();
                window.open(supportPortalUrl, '_blank', 'noopener');
              }}
            >
              <HeadsetIcon size={18} />
              Support Portal
            </Button>
            {!v2 && (
              <Button
                variant="menu"
                className={LIST_ROW}
                onPress={() => {
                  close();
                  navigate('/app/settings?tab=shipnode');
                }}
              >
                <PrinterIcon size={18} />
                ShipNode
              </Button>
            )}
            <Button
              variant="menu"
              className={LIST_ROW}
              onPress={() => {
                close();
                window.open(knowledgeBaseUrl, '_blank', 'noopener');
              }}
            >
              <BookOpenIcon size={18} />
              Knowledge Base
            </Button>
            {!v2 && (
              <Button
                variant="menu"
                className={LIST_ROW}
                onPress={() => {
                  close();
                  openExportPopup();
                }}
              >
                <TableIcon size={18} />
                Export Prediction Report
              </Button>
            )}
            <Button
              variant="menu"
              className={cn(LIST_ROW, 'sk:text-danger')}
              onPress={() => {
                close();
                logout();
              }}
            >
              <SignOutIcon size={18} />
              Logout
            </Button>
          </div>
        )}
      </Popover>
    </div>
  );
}
